import imageFilesData from "../data/imageFiles.json";

const folderMap = {
  BACKGROUND: "0 BACKGROUND",
  "BACKGROUND ACC": "1 BACKGROUND ACC",
  BASE: (gender) => (gender === "male" ? "2 MALE BASE" : "2 FEMALE BASE"),
  OUTFIT: (gender) => (gender === "male" ? "3 MALE OUTFIT" : "3 FEMALE OUTFIT"),
  HAIR: (gender) => (gender === "male" ? "4 MALE HAIR" : "4 FEMALE HAIR"),
  EYE: (gender) => (gender === "male" ? "5 MALE EYE" : "5 FEMALE EYE"),
  EYEBROW: (gender) =>
    gender === "male" ? "6 MALE EYEBROW" : "6 FEMALE EYEBROW",
  MOUTH: "7 MOUTH",
  "MONAD LOGO": "8 MONAD LOGO",
  HAND: "9 HAND",
  "HAND BASE": "10 HAND BASE",
  "FACE ACC": "11 FACE ACC",
  "HAIR ACC": "12 HAIR ACC",
};

// 检查浏览器是否支持 WebP
const supportsWebP = () => {
  const canvas = document.createElement("canvas");
  if (!canvas.toDataURL) return false;
  return canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
};

// 获取最适合的图片格式
const getBestImageFormat = (originalPath) => {
  if (!originalPath) return originalPath;

  // 如果已经是优化路径，直接返回
  if (originalPath.includes('/optimized/') || originalPath.includes('/webp/')) {
    return originalPath;
  }

  // 优先使用优化版本
  const optimizedPath = originalPath.replace(/^\//, '/optimized/');
  return optimizedPath;
};

export const getImagePaths = (gender, traitType) => {
  const files = imageFilesData[gender]?.[traitType] || [];
  return files;
};

// 获取特征（traits）图片路径列表
export const getTraitsPaths = (gender, traitType) => {
  const traitCategory = `${traitType} TRAITS`;
  const files = imageFilesData[gender]?.[traitCategory] || [];
  return files;
};

// 获取优化的图片路径列表
export const getOptimizedImagePaths = (gender, traitType) => {
  const files = getImagePaths(gender, traitType);
  return files.map(getBestImageFormat);
};

// 获取单个优化图片路径
export const getOptimizedImagePath = (originalPath) => {
  return getBestImageFormat(originalPath);
};

export const getAllTraits = () => {
  const traitTypes = [
    "BACKGROUND",
    "BACKGROUND ACC",
    "BASE",
    "OUTFIT",
    "HAIR",
    "EYE",
    "EYEBROW",
    "MOUTH",
    "MONAD LOGO",
    "HAND",
    "HAND BASE",
    "FACE ACC",
    "HAIR ACC",
  ];

  const maleTraits = {};
  const femaleTraits = {};

  traitTypes.forEach((type) => {
    // 获取所有特征，包括主要特征和变体特征
    const malePaths = [
      ...getImagePaths("male", type),
      ...getTraitsPaths("male", type)
    ].map(getBestImageFormat);

    const femalePaths = [
      ...getImagePaths("female", type),
      ...getTraitsPaths("female", type)
    ].map(getBestImageFormat);
    
    // 去重
    if (malePaths.length > 0) maleTraits[type] = [...new Set(malePaths)];
    if (femalePaths.length > 0) femaleTraits[type] = [...new Set(femalePaths)];
  });

  return { maleTraits, femaleTraits };
};

// 创建一个图片预加载器，处理回退逻辑
export const createImageWithFallback = (originalPath) => {
  return new Promise((resolve) => {
    if (!originalPath) {
      resolve(originalPath);
      return;
    }

    const img = new Image();

    // 如果是下载操作，直接使用原始图片
    if (window.isDownloadingPfp) {
      img.onload = () => resolve(originalPath);
      img.onerror = () => resolve(originalPath);
      img.src = originalPath;
      return;
    }

    // 如果路径已经包含 webp 或 optimized，直接使用
    if (originalPath.includes('/webp/') || originalPath.includes('/optimized/')) {
      img.onload = () => resolve(originalPath);
      img.onerror = () => resolve(originalPath);
      img.src = originalPath;
      return;
    }

    // 首先尝试高质量的 WebP 优化版本
    const webpPath = originalPath
      .replace(/\.(jpg|jpeg|png)$/i, ".webp")
      .replace(/^\//, "/webp/");

    img.onload = () => {
      // 检查加载的图片质量
      if (img.width >= 500 && img.height >= 500) {
        resolve(webpPath);
      } else {
        // 如果WebP版本分辨率太低，使用原始图片
        const originalImg = new Image();
        originalImg.onload = () => resolve(originalPath);
        originalImg.onerror = () => resolve(webpPath); // 如果原始图片加载失败，回退到WebP
        originalImg.src = originalPath;
      }
    };
    img.onerror = () => {
      // WebP 失败，尝试压缩版本
      const compressedPath = originalPath.replace(/^\//, "/optimized/");
      const fallbackImg = new Image();

      fallbackImg.onload = () => {
        // 检查压缩版本的质量
        if (fallbackImg.width >= 500 && fallbackImg.height >= 500) {
          resolve(compressedPath);
        } else {
          // 如果压缩版本质量太低，使用原始图片
          const originalImg = new Image();
          originalImg.onload = () => resolve(originalPath);
          originalImg.onerror = () => resolve(compressedPath); // 如果原始图片加载失败，使用压缩版本
          originalImg.src = originalPath;
        }
      };
      fallbackImg.onerror = () => {
        // 所有优化版本都失败，使用原始路径
        resolve(originalPath);
      };

      fallbackImg.src = compressedPath;
    };

    img.src = webpPath;
  });
};

// 获取优化的所有特征
export const getAllOptimizedTraits = () => {
  const traitTypes = [
    "BACKGROUND",
    "BACKGROUND ACC",
    "BASE",
    "OUTFIT",
    "HAIR",
    "EYE",
    "EYEBROW",
    "MOUTH",
    "MONAD LOGO",
    "HAND",
    "HAND BASE",
    "FACE ACC",
    "HAIR ACC",
  ];

  const maleTraits = {};
  const femaleTraits = {};

  traitTypes.forEach((type) => {
    maleTraits[type] = getOptimizedImagePaths("male", type);
    femaleTraits[type] = getOptimizedImagePaths("female", type);
  });

  return { maleTraits, femaleTraits };
};
